import React, { createContext, useContext, useState, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, addDoc, getDocs, query, where, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const ReviewContext = createContext();

export function useReview() {
  return useContext(ReviewContext);
}

export function ReviewProvider({ children }) {
  const [reviews, setReviews] = useState({}); // { businessId: [reviews] }
  const [adminReviews, setAdminReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAdminReviews, setLoadingAdminReviews] = useState(false);
  const { currentUser } = useAuth();

  // Charger les avis pour une entreprise
  const loadReviews = useCallback(async (businessId) => {
    try {
      setLoading(true);
      const reviewsRef = collection(db, 'reviews');
      const q = query(
        reviewsRef,
        where('businessId', '==', businessId),
        where('isApproved', '==', true),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const reviewsData = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      setReviews((prev) => ({
        ...prev,
        [businessId]: reviewsData
      }));

      return reviewsData;
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
      const savedReviews = localStorage.getItem(`reviews_${businessId}`);
      if (savedReviews) {
        const localReviews = JSON.parse(savedReviews).filter((r) => r.isApproved !== false);
        setReviews((prev) => ({
          ...prev,
          [businessId]: localReviews
        }));
        return localReviews;
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getAverageRating = useCallback((businessId) => {
    const businessReviews = reviews[businessId] || [];
    if (businessReviews.length === 0) return 0;

    const sum = businessReviews.reduce((acc, review) => acc + (review.rating || 0), 0);
    return sum / businessReviews.length;
  }, [reviews]);

  const getReviewCount = useCallback((businessId) => {
    return reviews[businessId]?.length || 0;
  }, [reviews]);

  const loadAdminReviews = useCallback(async () => {
    setLoadingAdminReviews(true);
    try {
      try {
        const q = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        setAdminReviews(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (orderedError) {
        console.warn('Chargement avis admin sans orderBy:', orderedError?.message);
        const snapshot = await getDocs(collection(db, 'reviews'));
        const rows = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        rows.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
        setAdminReviews(rows);
      }
    } catch (error) {
      console.error('Erreur chargement avis admin:', error);
      toast.error('Impossible de charger les avis');
      setAdminReviews([]);
    } finally {
      setLoadingAdminReviews(false);
    }
  }, []);

  const submitReview = async (reviewData) => {
    try {
      setLoading(true);

      const review = {
        businessId: reviewData.businessId,
        businessName: reviewData.businessName,
        userId: currentUser?.uid || 'anonymous',
        userName: currentUser?.displayName || currentUser?.email || 'Utilisateur',
        userEmail: currentUser?.email || '',
        rating: Number(reviewData.rating),
        comment: reviewData.comment || '',
        images: reviewData.images || [],
        createdAt: new Date().toISOString(),
        isApproved: false,
        helpfulCount: 0,
        reportedCount: 0
      };

      try {
        const docRef = await addDoc(collection(db, 'reviews'), review);
        review.id = docRef.id;
      } catch (firebaseError) {
        console.warn('Erreur Firebase, utilisation du stockage local:', firebaseError);
        const savedReviews = JSON.parse(localStorage.getItem(`reviews_${reviewData.businessId}`) || '[]');
        review.id = `local_${Date.now()}`;
        savedReviews.push(review);
        localStorage.setItem(`reviews_${reviewData.businessId}`, JSON.stringify(savedReviews));
      }

      toast.success('Avis soumis — en attente de validation');

      // Points attribués côté serveur après validation admin (anti-farming).

      return review;
    } catch (error) {
      console.error('Erreur lors de la soumission de l\'avis:', error);
      toast.error('Erreur lors de la soumission de l\'avis');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const approveReview = async (reviewId) => {
    try {
      await updateDoc(doc(db, 'reviews', reviewId), {
        isApproved: true,
        isRejected: false,
        moderatedAt: new Date().toISOString()
      });
      setAdminReviews((prev) =>
        prev.map((item) =>
          item.id === reviewId ? { ...item, isApproved: true, isRejected: false } : item
        )
      );
      toast.success('Avis approuvé');
      return true;
    } catch (error) {
      console.error('Erreur approbation avis:', error);
      toast.error('Impossible d’approuver cet avis');
      return false;
    }
  };

  const rejectReview = async (reviewId) => {
    try {
      await updateDoc(doc(db, 'reviews', reviewId), {
        isApproved: false,
        isRejected: true,
        moderatedAt: new Date().toISOString()
      });
      setAdminReviews((prev) =>
        prev.map((item) =>
          item.id === reviewId ? { ...item, isApproved: false, isRejected: true } : item
        )
      );
      toast.success('Avis rejeté');
      return true;
    } catch (error) {
      console.error('Erreur rejet avis:', error);
      toast.error('Impossible de rejeter cet avis');
      return false;
    }
  };

  const markHelpful = async (reviewId, businessId) => {
    try {
      const review = reviews[businessId]?.find((r) => r.id === reviewId);
      if (!review) return;

      const updatedReview = {
        ...review,
        helpfulCount: (review.helpfulCount || 0) + 1
      };

      try {
        await updateDoc(doc(db, 'reviews', reviewId), {
          helpfulCount: updatedReview.helpfulCount
        });
      } catch (error) {
        const savedReviews = JSON.parse(localStorage.getItem(`reviews_${businessId}`) || '[]');
        const index = savedReviews.findIndex((r) => r.id === reviewId);
        if (index !== -1) {
          savedReviews[index] = updatedReview;
          localStorage.setItem(`reviews_${businessId}`, JSON.stringify(savedReviews));
        }
      }

      setReviews((prev) => ({
        ...prev,
        [businessId]: prev[businessId].map((r) => (r.id === reviewId ? updatedReview : r))
      }));
    } catch (error) {
      console.error('Erreur lors du marquage comme utile:', error);
    }
  };

  const reportReview = async (reviewId, businessId) => {
    try {
      const review = reviews[businessId]?.find((r) => r.id === reviewId);
      if (!review) return;

      const updatedReview = {
        ...review,
        reportedCount: (review.reportedCount || 0) + 1
      };

      try {
        await updateDoc(doc(db, 'reviews', reviewId), {
          reportedCount: updatedReview.reportedCount
        });
      } catch (error) {
        const savedReviews = JSON.parse(localStorage.getItem(`reviews_${businessId}`) || '[]');
        const index = savedReviews.findIndex((r) => r.id === reviewId);
        if (index !== -1) {
          savedReviews[index] = updatedReview;
          localStorage.setItem(`reviews_${businessId}`, JSON.stringify(savedReviews));
        }
      }

      toast.success('Avis signalé. Merci pour votre retour.');
    } catch (error) {
      console.error('Erreur lors du signalement:', error);
      toast.error('Erreur lors du signalement');
    }
  };

  const deleteReview = async (reviewId, businessId) => {
    try {
      try {
        await deleteDoc(doc(db, 'reviews', reviewId));
      } catch (error) {
        const savedReviews = JSON.parse(localStorage.getItem(`reviews_${businessId}`) || '[]');
        const filtered = savedReviews.filter((r) => r.id !== reviewId);
        localStorage.setItem(`reviews_${businessId}`, JSON.stringify(filtered));
      }

      setReviews((prev) => ({
        ...prev,
        [businessId]: (prev[businessId] || []).filter((r) => r.id !== reviewId)
      }));
      setAdminReviews((prev) => prev.filter((item) => item.id !== reviewId));

      toast.success('Avis supprimé');
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression');
      return false;
    }
  };

  const getRatingDistribution = (businessId) => {
    const businessReviews = reviews[businessId] || [];
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    businessReviews.forEach((review) => {
      const rating = Math.round(review.rating);
      if (distribution[rating] !== undefined) {
        distribution[rating]++;
      }
    });

    return distribution;
  };

  const getPendingReviews = useCallback(() => {
    return adminReviews.filter((item) => item.isApproved !== true && item.isRejected !== true);
  }, [adminReviews]);

  const value = {
    reviews,
    adminReviews,
    loading,
    loadingAdminReviews,
    loadReviews,
    loadAdminReviews,
    submitReview,
    approveReview,
    rejectReview,
    getAverageRating,
    getReviewCount,
    getPendingReviews,
    markHelpful,
    reportReview,
    deleteReview,
    getRatingDistribution
  };

  return (
    <ReviewContext.Provider value={value}>
      {children}
    </ReviewContext.Provider>
  );
}
